// Single responsibility: render dashboard search combobox and result list UI.
import { useEffect, useId, useLayoutEffect, useRef } from "react";

import { RESULT_TYPES, SEARCH_MIN_QUERY_LENGTH, SEARCH_PLACEHOLDER } from "./searchConstants";

export default function DashboardSearchCombobox({
  query,
  onQueryChange,
  results,
  selectedIndex,
  setSelectedIndex,
  onSelectResult,
  onKeyDown,
  onOpen,
  onClose,
  isOpen,
  isSearching,
  isDarkMode = false,
  isMobile = false,
  placeholder = SEARCH_PLACEHOLDER,
  inputId,
  /** When set, outside-click ignores targets inside this element (e.g. header tray + toggle). */
  rootBoundaryRef = null,
  /** Focus input after mount (desktop header dock). */
  autoFocus = false,
  /** Widen results panel to the left for narrow header-docked field. */
  dockResults = false,
}) {
  const generatedInputId = useId().replace(/:/g, "");
  const resolvedInputId = inputId || `ed-dash-search-${generatedInputId}`;
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    if (!autoFocus) return;
    inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const t = event.target;
      const root = rootBoundaryRef?.current;
      if (rootBoundaryRef != null) {
        if (root && !root.contains(t)) onClose();
        return;
      }
      if (!isOpen) return;
      if (!containerRef.current?.contains(t)) onClose();
    };

    if (rootBoundaryRef != null) {
      document.addEventListener("mousedown", handlePointerDown);
      return () => document.removeEventListener("mousedown", handlePointerDown);
    }
    if (!isOpen) return undefined;
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen, onClose, rootBoundaryRef]);

  useEffect(() => {
    if (!rootBoundaryRef && !isOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, rootBoundaryRef]);

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <label htmlFor={resolvedInputId} className="ed-visually-hidden">
        {placeholder}
      </label>
      <input
        ref={inputRef}
        id={resolvedInputId}
        type="search"
        className="ed-dash-top-header__search-input"
        placeholder={placeholder}
        value={query}
        onChange={(event) => {
          onQueryChange(event.target.value);
          onOpen();
        }}
        onFocus={onOpen}
        onKeyDown={onKeyDown}
        autoComplete="off"
        aria-label={placeholder}
        aria-controls={`${resolvedInputId}-results`}
      />
      {isOpen && results.length > 0 ? (
        <div
          id={`${resolvedInputId}-results`}
          role="listbox"
          style={{
            position: "absolute",
            ...(dockResults
              ? { left: "auto", right: 0, width: "min(18rem, calc(100vw - 2rem))", maxWidth: "calc(100vw - 1rem)" }
              : { left: 0, right: 0 }),
            top: "calc(100% + 0.35rem)",
            zIndex: 140,
            borderRadius: 12,
            border: "1px solid rgba(56, 189, 248, 0.28)",
            background: isDarkMode ? "rgba(15, 23, 42, 0.97)" : "rgba(255, 255, 255, 0.98)",
            boxShadow: isDarkMode ? "0 20px 46px rgba(2,6,23,0.7)" : "0 14px 34px rgba(15, 23, 42, 0.18)",
            backdropFilter: "blur(8px)",
            overflow: "hidden",
            maxHeight: isMobile ? "52vh" : "420px",
            overflowY: "auto",
          }}
        >
          {results.map((result, index) => (
            <button
              key={result.id}
              type="button"
              role="option"
              aria-selected={index === selectedIndex}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => onSelectResult(result)}
              style={{
                width: "100%",
                textAlign: "left",
                border: 0,
                borderTop: index === 0 ? 0 : `1px solid ${isDarkMode ? "rgba(148,163,184,0.14)" : "rgba(148,163,184,0.18)"}`,
                background:
                  index === selectedIndex
                    ? isDarkMode
                      ? "rgba(30,41,59,0.85)"
                      : "rgba(224, 242, 254, 0.65)"
                    : "transparent",
                color: isDarkMode ? "#e2e8f0" : "#0f172a",
                padding: "0.66rem 0.78rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.65rem",
                cursor: "pointer",
              }}
            >
              <span style={{ minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontSize: "0.84rem",
                    fontWeight: 700,
                    lineHeight: 1.3,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {result.label}
                </span>
                {result.sublabel ? (
                  <span
                    style={{
                      display: "block",
                      fontSize: "0.74rem",
                      opacity: 0.82,
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {result.sublabel}
                  </span>
                ) : null}
              </span>
              <span
                style={{
                  flexShrink: 0,
                  borderRadius: 999,
                  fontSize: "0.64rem",
                  padding: "0.18rem 0.45rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  background: isDarkMode ? "rgba(14,165,233,0.16)" : "rgba(14,165,233,0.12)",
                  color: isDarkMode ? "#7dd3fc" : "#0369a1",
                }}
              >
                {result.type === RESULT_TYPES.INVENTORY ? "Inventory" : "Action"}
              </span>
            </button>
          ))}
          <div
            style={{
              padding: "0.4rem 0.65rem 0.5rem",
              fontSize: "0.66rem",
              letterSpacing: "0.02em",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              borderTop: `1px solid ${isDarkMode ? "rgba(148,163,184,0.14)" : "rgba(148,163,184,0.2)"}`,
            }}
          >
            Enter opens best match · ↑ ↓ to browse · Escape to close
          </div>
        </div>
      ) : null}
      {isOpen && query.length >= SEARCH_MIN_QUERY_LENGTH && results.length === 0 ? (
        <div
          id={`${resolvedInputId}-results`}
          role="status"
          style={{
            position: "absolute",
            ...(dockResults
              ? { left: "auto", right: 0, width: "min(18rem, calc(100vw - 2rem))", maxWidth: "calc(100vw - 1rem)" }
              : { left: 0, right: 0 }),
            top: "calc(100% + 0.35rem)",
            zIndex: 140,
            borderRadius: 12,
            border: "1px solid rgba(56, 189, 248, 0.28)",
            background: isDarkMode ? "rgba(15, 23, 42, 0.97)" : "rgba(255, 255, 255, 0.98)",
            boxShadow: isDarkMode ? "0 20px 46px rgba(2,6,23,0.7)" : "0 14px 34px rgba(15, 23, 42, 0.18)",
            backdropFilter: "blur(8px)",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "0.8rem 0.9rem", fontSize: "0.82rem", color: isDarkMode ? "#94a3b8" : "#64748b" }}>
            No results for &quot;{query}&quot;
          </div>
        </div>
      ) : null}
      {isSearching ? (
        <span className="ed-visually-hidden" aria-live="polite">
          Searching
        </span>
      ) : null}
      <button
        type="button"
        onClick={onClose}
        className="ed-visually-hidden"
        tabIndex={-1}
        aria-hidden
      >
        Close search
      </button>
    </div>
  );
}
